import { computed } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import {
    useApi,
    type ChangeExpertPasswordRequest,
    type RequestExpertEmailChangeRequest,
    type UpdateExpertProfileRequest,
    type VerifyExpertEmailChangeRequest,
} from "@shopexperts/shared";
import { AUTH_CHECK_QUERY_KEY } from "@/app/stores/clientAuth";

export function useExpertProfile() {
    const api = useApi();
    const queryClient = useQueryClient();

    const PROFILE_QUERY_KEY = ["expert", "profile"] as const;

    const {
        data: profileData,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: PROFILE_QUERY_KEY,
        queryFn: () => api.expert.profile.getProfile(),
        staleTime: 1000 * 60 * 5,
    });

    const profile = computed(() => profileData.value ?? null);
    const user = computed(() => profileData.value?.user ?? null);
    const userType = computed(() => profileData.value?.user_type ?? null);
    const expert = computed(() => profileData.value?.expert ?? null);
    const freelancer = computed(() => profileData.value?.freelancer ?? null);
    const agency = computed(() => profileData.value?.agency ?? null);
    const socialLinks = computed(() => profileData.value?.social_links ?? null);
    const address = computed(() => profileData.value?.address ?? null);
    const pendingEmailChange = computed(() => profileData.value?.pending_email_change ?? null);
    const subscriptionSummary = computed(() => profileData.value?.subscription_summary ?? null);

    const fullName = computed(() => {
        if (userType.value === "agency") {
            return agency.value?.agency_name || agency.value?.representative_name || "";
        }

        return `${freelancer.value?.first_name || ""} ${freelancer.value?.last_name || ""}`.trim();
    });

    const initials = computed(() => {
        const source = fullName.value || user.value?.email || "";
        if (!source) return "";
        const parts = source.trim().split(/\s+/);
        return parts
            .slice(0, 2)
            .map((part) => part.charAt(0))
            .join("")
            .toUpperCase();
    });

    const updateProfileMutation = useMutation({
        mutationFn: (data: UpdateExpertProfileRequest) => api.expert.profile.updateProfile({ body: data }),
        onSuccess: (newProfile) => {
            queryClient.setQueryData(PROFILE_QUERY_KEY, newProfile);
            queryClient.invalidateQueries({ queryKey: AUTH_CHECK_QUERY_KEY });
        },
    });

    const changePasswordMutation = useMutation({
        mutationFn: (data: ChangeExpertPasswordRequest) =>
            api.expert.profile.changePassword({ body: data }),
    });

    const requestEmailChangeMutation = useMutation({
        mutationFn: (data: RequestExpertEmailChangeRequest) =>
            api.expert.profile.requestEmailChange({ body: data }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
        },
    });

    const verifyEmailChangeMutation = useMutation({
        mutationFn: (data: VerifyExpertEmailChangeRequest) =>
            api.expert.profile.verifyEmailChange({ body: data }),
    });

    async function updateProfile(data: UpdateExpertProfileRequest) {
        return updateProfileMutation.mutateAsync(data);
    }

    async function changePassword(currentPassword: string, newPassword: string) {
        return changePasswordMutation.mutateAsync({
            current_password: currentPassword,
            new_password: newPassword,
        });
    }

    async function requestEmailChange(newEmail: string, password: string) {
        return requestEmailChangeMutation.mutateAsync({
            new_email: newEmail,
            password,
        });
    }

    async function verifyEmailChange(token: string, email: string) {
        return verifyEmailChangeMutation.mutateAsync({
            token,
            email,
        });
    }

    return {
        profile,
        user,
        userType,
        expert,
        freelancer,
        agency,
        socialLinks,
        address,
        pendingEmailChange,
        subscriptionSummary,
        fullName,
        initials,
        isLoading,
        error,
        isUpdating: updateProfileMutation.isPending,
        isChangingPassword: changePasswordMutation.isPending,
        isRequestingEmailChange: requestEmailChangeMutation.isPending,
        isVerifyingEmailChange: verifyEmailChangeMutation.isPending,
        updateError: updateProfileMutation.error,
        passwordError: changePasswordMutation.error,
        emailChangeError: requestEmailChangeMutation.error,
        verifyEmailError: verifyEmailChangeMutation.error,
        refetch,
        updateProfile,
        changePassword,
        requestEmailChange,
        verifyEmailChange,
    };
}
